'use client'

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { cn, dateFormat } from '@utils/safeTs'
import { ChevronDown, Remove } from '@components/icons'
import styles from './Filter.module.sass'
import FormCheckbox from '../FormCheckbox/FormCheckbox'
import useTrans from '@hooks/useTrans'
import FormRadio from '../FormRadio/FormRadio'
import FormField from '../FormField/FormField'
import FormInput from '../FormInput/FormInput'
import Button from '../Button/Button'
import dayjs from 'dayjs'

export type FilterVariant = 'single' | 'multiple' | 'dateRange'

export interface FilterOption {
  label: string
  value: string
}

export interface FilterQuickOption extends FilterOption {
  from?: string
  to?: string
}

export interface FilterDateRangeValue {
  from?: string
  to?: string
  preset?: string
  label?: string
}

export type FilterValue = string | string[] | FilterDateRangeValue | undefined

export interface FilterProps {
  id: string
  className?: string
  label?: string
  keepLabel?: boolean
  variant?: FilterVariant
  optionsVariant?: '' | 'grid'
  options?: FilterOption[]
  value?: FilterValue
  onChange?: (value: FilterValue) => void
  disabled?: boolean
  dropdownSide?: 'left' | 'right'
  showReset?: boolean
  maxDateRangeMonths?: number
}

const Filter = ({
  id,
  className,
  label,
  keepLabel = false,
  variant = 'single',
  optionsVariant = '',
  options = [],
  value,
  onChange,
  disabled = false,
  dropdownSide = 'right',
  showReset = true,
  maxDateRangeMonths,
}: FilterProps) => {

  const t = useTrans()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownId = useId()

  const stdDateOptions = [
    { label: t("Ultima settimana"), value: 'last_week' },
    { label: t("Ultimo mese"), value: 'last_month' },
    { label: t("Ultimi 6 mesi"), value: 'last_6months' },
    { label: t("Personalizzato"), value: 'custom' },
  ]
  const dateOptions = useMemo(() => variant === 'dateRange' && options?.length ? options : stdDateOptions, [variant, options])

  const [selectedDateOpt, setSelectedDateOpt] = useState(variant === 'dateRange' ? value?.[0] : null)
  const [dateRangeFromValue, setDateRangeFromValue] = useState(value?.[0] === 'custom' ? value?.[1] : undefined)
  const [dateRangeToValue, setDateRangeToValue] = useState(value?.[0] === 'custom' ? value?.[2] : undefined)

  useEffect(() => {
    if (!open) return undefined
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    const handleFocusIn = (event: FocusEvent) => {
      if (!!containerRef.current && !containerRef.current.contains(event.target as Node))
        setOpen(false)
    }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('focusIn', handleFocusIn)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [open])

  const multiValue = Array.isArray(value) ? value : []
  const customDateRangeIsValid = !maxDateRangeMonths || (
    !!dateRangeFromValue &&
    !!dateRangeToValue &&
    !dayjs(dateRangeToValue).isBefore(dayjs(dateRangeFromValue), 'day') &&
    !dayjs(dateRangeToValue).isAfter(dayjs(dateRangeFromValue).add(maxDateRangeMonths, 'month').subtract(1, 'day'), 'day')
  )
  const today = dayjs().format('YYYY-MM-DD')
  const dateRangeFromMin = maxDateRangeMonths && dateRangeToValue
    ? dayjs(dateRangeToValue).subtract(maxDateRangeMonths, 'month').add(1, 'day').format('YYYY-MM-DD')
    : undefined
  let dateRangeToMax = today
  if (maxDateRangeMonths && dateRangeFromValue) {
    const maxDateRangeTo = dayjs(dateRangeFromValue).add(maxDateRangeMonths, 'month').subtract(1, 'day')
    dateRangeToMax = (maxDateRangeTo.isBefore(dayjs(), 'day') ? maxDateRangeTo : dayjs()).format('YYYY-MM-DD')
  }

  const getDefaultLabel = () => {

    if (variant === 'single' && !!value) {
      return options.find((option) => option.value === value)?.label
    }

    if (variant === 'multiple' && multiValue.length) {
      const labels = multiValue
        .map((item) => options.find((option) => option.value === item)?.label)
        .filter(Boolean) as string[]
      if (labels.length === 1) return labels[0]
      if (labels.length === 2) return labels.join(', ')
      if (labels.length > 2) return `${labels.length} selezionati`
    }

    if (variant === 'dateRange' && multiValue.length) {
      if (selectedDateOpt !== 'custom') return dateOptions.filter(x => x.value === selectedDateOpt)[0].label
      if (multiValue?.[1] && multiValue?.[2]) return `${dateFormat(multiValue[1])} - ${dateFormat(multiValue[2])}`
      if (multiValue?.[1]) return `Dal ${dateFormat(multiValue[1])}`
      if (multiValue?.[2]) return `Al ${dateFormat(multiValue[2])}`
    }

    return undefined
  }

  const resolvedLabel = getDefaultLabel()
  const displayLabel = keepLabel ? label : resolvedLabel ?? label

  const triggerClick = () => {  
    if (variant === 'single' && !!value && showReset)
      resetFilter()
    else 
      setOpen((prev) => !prev)
  }

  const closeDropdown = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const handleSelectSingle = (option: FilterOption) => {
    onChange?.(option.value)
    closeDropdown()
  }

  const handleToggleMultiple = (option: FilterOption) => {
    const multiValue = Array.isArray(value) ? value : []
    const next = multiValue.includes(option.value)
      ? multiValue.filter((item) => item !== option.value)
      : [...multiValue, option.value]
    onChange?.(next?.length ? next : undefined)
  }

  useEffect(() => {
    if (!!selectedDateOpt){
      if (selectedDateOpt !== 'custom'){
        onChange([selectedDateOpt])
        closeDropdown()
      }
    }
  }, [selectedDateOpt])


  const resetFilter = () => {
    onChange(null)
    setSelectedDateOpt(null)
    setDateRangeFromValue(null)
    setDateRangeToValue(null)
    closeDropdown()
  }

  return (
    <div
      className={cn(styles.root, styles[`root--${variant}`], styles[`root--options-${optionsVariant}`], styles[`root--dropdown-${dropdownSide}`], className)}
      ref={containerRef}
    >
      <button
        ref={triggerRef}
        type='button'
        className={styles.trigger}
        onClick={triggerClick}
        aria-haspopup={variant === 'dateRange' ? 'dialog' : 'listbox'}
        aria-expanded={open}
        aria-controls={open ? dropdownId : undefined}
        disabled={disabled}
      >
        <span className={cn(styles.triggerLabel, value ? styles['triggerLabel--full'] : null)}>
          {!!value && !keepLabel && (
            <span className={styles.label}>
              {label}
            </span>
          )}
          <span>{displayLabel}</span>
        </span>
        {variant === 'single' && !!value && !!showReset ? <Remove className={styles.removeIcon} /> : <ChevronDown className={styles.triggerArrow} aria-hidden />}
      </button>
      {open ? (
        <div
          id={dropdownId}
          role={variant === 'dateRange' ? 'dialog' : 'listbox'}
          aria-multiselectable={variant === 'multiple' ? true : undefined}
          className={styles.dropdown}
        >
          {['single', 'multiple'].includes(variant) && options.length === 0 ? (
            <p className={styles.empty}>{t("Nessuna opzione disponibile")}</p>
          ) : null}

          {variant === 'single'
            ? options.map((option) => {
                const selected = option.value === value
                return (
                  <button
                    key={option.value}
                    type='button'
                    className={cn(styles.option, selected && styles['option--selected'])}
                    role='option'
                    aria-selected={selected}
                    onClick={() => handleSelectSingle(option)}
                  >
                    {option.label}
                  </button>
                )
              })
            : null}

          {variant === 'multiple'
            ? options.map((option) => (
              <FormCheckbox
                key={`${id}_${option.value}`}
                id={`${id}_${option.value}`}
                size='sm'
                checked={Array.isArray(value) ? value.includes(option.value) : false}
                label={option.label}
                value={option.value}
                onChange={() => handleToggleMultiple(option)}
                className={styles.multiOption}
              />
            ))
            : null}

          {variant === 'dateRange' ? (
            <div className={styles.dateRangeContent}>
              <div className={styles.dateOptions}>
                {dateOptions.map((opt) => (
                  <FormRadio
                    key={`${id}_${opt.value}`}
                    id={`${id}_${opt.value}`}
                    label={opt.label}
                    size='sm'
                    name={'dateOption[]'}
                    onClick={() => setSelectedDateOpt(opt.value)}
                    checked={opt.value === selectedDateOpt}
                  />
                ))}
              </div>
              {selectedDateOpt === 'custom' && (
                <div className={styles.customContent}>
                  <FormField label={t("Dal")}>
                    <FormInput
                      id={`${id}_dal`}
                      type="date"
                      value={dateRangeFromValue}
                      onChange={(e) => setDateRangeFromValue(e.target.value)}
                      placeholder={'DD/MM/YYYY'}
                      min={dateRangeFromMin}
                      max={today}
                    />
                  </FormField>
                  <FormField label={t("Al")}>
                    <FormInput
                      id={`${id}_al`}
                      type="date"
                      value={dateRangeToValue}
                      onChange={(e) => setDateRangeToValue(e.target.value)}
                      placeholder={'DD/MM/YYYY'}
                      min={dateRangeFromValue || undefined}
                      max={dateRangeToMax}
                    />
                  </FormField>
                </div>
              )}
            </div>
          ) : null}

          {['dateRange', 'multiple'].includes(variant) && ((!!value && showReset) || (variant === 'dateRange' && selectedDateOpt === 'custom')) && (
            <div className={styles.ctaBar}>
              {!!value && showReset && (
                <Button 
                  label={t("Rimuovi")} 
                  variant="secondary"
                  size="small"
                  onClick={resetFilter} 
                  disabled={variant === 'multiple' ? !value : !selectedDateOpt}
                />
              )}
              {selectedDateOpt === 'custom' && (
                <Button 
                  label={t("Applica")} 
                  variant="primary"
                  size="small"
                  onClick={() => {
                    onChange(['custom', dateRangeFromValue, dateRangeToValue])
                    closeDropdown()
                  }} 
                  className={styles.ctaConfirm}
                  disabled={selectedDateOpt === 'custom' && (!dateRangeFromValue || !dateRangeToValue || !customDateRangeIsValid)}
                />
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default Filter
